import { api, NotifyModelKey } from "./api";
import { makeCollapsible } from "./comments";
import { renderPostHocNotifyPrompt } from "./notifyEmail";

// Zakładka "Pytania na egzaminie" (na razie tylko WPiA UW, patrz supports_exam_dimensions w
// views.post_detail) - BEZ oceny/opinii (patrz ExamQuestion w models.py), więc znacznie prostsza
// niż jakikolwiek inny moduł komentarzy w serwisie: sama treść pytania + moderacja, bez gwiazdek/
// głosowania/zgłaszania/sortowania.

export type examQuestionStatus = "active" | "rejected" | "awaiting_moderation";

export interface ExamQuestion {
    id: number; text: string; created: string; status: examQuestionStatus; is_from_wpia_wiki: boolean;
}

// Osobisty checklist "przerobione" - WYŁĄCZNIE lokalnie w przeglądarce (serwis nie ma kont
// użytkowników, więc nie ma do czego przypiąć tego po stronie backendu - localStorage i tak
// spełnia "zapisuje się między sesjami", bo przeżywa zamknięcie karty/przeglądarki, w
// odróżnieniu od sessionStorage). Klucz per-profil (post), wartość to tablica ID odznaczonych
// pytań tego profilu.
const CHECKED_STORAGE_PREFIX = "examQuestionsChecked:";

function loadCheckedIds(postId: number): Set<number> {
    try {
        const raw = window.localStorage.getItem(`${CHECKED_STORAGE_PREFIX}${postId}`);
        if (!raw) return new Set();
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return new Set();
        return new Set(parsed.filter((id): id is number => typeof id === "number"));
    } catch {
        return new Set();
    }
}

function saveCheckedIds(postId: number, ids: Set<number>): void {
    try {
        window.localStorage.setItem(`${CHECKED_STORAGE_PREFIX}${postId}`, JSON.stringify(Array.from(ids)));
    } catch {
        // localStorage niedostępny (tryb prywatny, pełny limit itp.) - po prostu nie zapisujemy,
        // bez wywalania błędu - to tylko pomocniczy checklist, nie krytyczna funkcja.
    }
}

function setQuestionChecked(postId: number, questionId: number, checked: boolean): void {
    const ids = loadCheckedIds(postId);
    if (checked) ids.add(questionId); else ids.delete(questionId);
    saveCheckedIds(postId, ids);
}

// Zwykła linia z myślnikiem, NIE karta komentarza (na wyraźną prośbę - "nie w takich osobnych
// kuferkach") - dużo prostszy wygląd niż .comment (bez obramowania/paddingu/cienia), bo pytanie
// egzaminacyjne to sam tekst, bez oceny/autora/daty wartych osobnego wizualnego wyróżnienia.
// Kółko z lewej to osobisty checklist "przerobione/do zrobienia" (patrz setQuestionChecked wyżej) -
// kliknięcie robi pytanie szare/przekreślone, stan trzyma się per przeglądarka między sesjami.
export function renderExamQuestion(question: ExamQuestion, postId: number): HTMLElement {
    const line = document.createElement("div");
    line.className = "exam-question-line";
    line.dataset.id = String(question.id);

    const toggle = document.createElement("button");
    toggle.type = "button";
    toggle.className = "exam-question-check";
    toggle.setAttribute("role", "checkbox");
    toggle.setAttribute("aria-label", "Oznacz jako przerobione");

    function applyCheckedState(checked: boolean): void {
        toggle.setAttribute("aria-checked", String(checked));
        line.classList.toggle("exam-question-line-done", checked);
    }
    applyCheckedState(loadCheckedIds(postId).has(question.id));

    toggle.addEventListener("click", () => {
        const nowChecked = toggle.getAttribute("aria-checked") !== "true";
        applyCheckedState(nowChecked);
        setQuestionChecked(postId, question.id, nowChecked);
    });
    line.appendChild(toggle);

    const bullet = document.createElement("span");
    bullet.className = "exam-question-bullet";
    bullet.textContent = "–";
    bullet.setAttribute("aria-hidden", "true");
    line.appendChild(bullet);

    const text = document.createElement("span");
    text.className = "exam-question-text";
    text.style.whiteSpace = "pre-line";
    text.textContent = question.text;
    line.appendChild(text);

    return line;
}

const BULLET_LINE_RE = /^([*\-•])(\s?)/;

// Granice bieżącej linii (start/koniec/długość dopasowanego prefiksu bulleta) dla dowolnej
// pozycji kursora w polu - współdzielone przez handlery Enter/Backspace/Delete niżej, każdy
// potrzebuje wiedzieć, gdzie kończy się bullet "- "/"* "/"• " tej linii.
function lineBoundsAt(value: string, pos: number): { lineStart: number; lineEnd: number; prefixLen: number } {
    const lineStart = value.lastIndexOf("\n", pos - 1) + 1;
    const nextBreak = value.indexOf("\n", lineStart);
    const lineEnd = nextBreak === -1 ? value.length : nextBreak;
    const match = value.slice(lineStart, lineEnd).match(BULLET_LINE_RE);
    return { lineStart, lineEnd, prefixLen: match ? match[0].length : 0 };
}

// Auto-bullet w polu tekstowym - jak listy w Google Docs, ale ŚCIŚLE wymuszony (na wyraźną
// prośbę: "każdy enter ma dawać kreskę i nie da się jej usunąć"):
// - Enter ZAWSZE dokłada nowy bullet na kolejnej linii (kontynuuje znak bieżącej linii, gdyby
//   ktoś zmienił go na "*") - bez wyjątku "pusty bullet kończy listę", jak było wcześniej.
// - Shift+Enter jest zablokowany (traktowany jak zwykły Enter) - nie da się nim ominąć bulleta
//   miękkim podziałem linii.
// - Backspace/Delete są zablokowane, gdy dotykałyby bulleta danej linii (samego znaku, spacji po
//   nim, albo scalenia z sąsiednią linią przez skasowanie znaku nowej linii) - to właśnie scalenie
//   dwóch bulletowanych linii w jedną jest najczęstszym sposobem "ukradkowego" zniknięcia bulleta,
//   bo backend (split_question_lines w pages/api/exam_questions.py) czyta bullet tylko na
//   POCZĄTKU linii.
// - Zaznaczenie (nie tylko kursor) obejmujące fragment bulleta LUB przechodzące przez granicę
//   linii jest też blokowane przy Backspace/Delete.
// - Jako siatka bezpieczeństwa na ścieżki, których żaden keydown nie złapie (wklejanie, wycinanie,
//   przeciągnij-i-upuść, IME, wpisanie znaku na zaznaczenie obejmujące bullet) - handler "input"
//   niżej dokłada bullet z powrotem na każdą linię, która go traci, tuż po zmianie.
function attachAutoBulletList(textArea: HTMLTextAreaElement): void {
    // defaultValue (nie tylko value) - żeby natywny form.reset() po udanym wysłaniu (patrz submit
    // handler niżej) przywracał "- " zamiast pustego pola, bez czego auto-bullet działałby tylko
    // przy pierwszym otwarciu formularza.
    textArea.defaultValue = "- ";

    textArea.addEventListener("keydown", (event) => {
        const value = textArea.value;
        const start = textArea.selectionStart;
        const end = textArea.selectionEnd;

        if (event.key === "Enter") {
            event.preventDefault();
            const currentLine = value.slice(lineBoundsAt(value, start).lineStart, start);
            const bulletMatch = currentLine.match(BULLET_LINE_RE);
            const bulletChar = bulletMatch ? bulletMatch[1] : "-";
            const insertion = `\n${bulletChar} `;
            textArea.value = value.slice(0, start) + insertion + value.slice(end);
            const newPos = start + insertion.length;
            textArea.selectionStart = textArea.selectionEnd = newPos;
            return;
        }

        if (event.key !== "Backspace" && event.key !== "Delete") return;

        if (start !== end) {
            const { lineStart, prefixLen } = lineBoundsAt(value, start);
            const crossesLine = value.slice(start, end).includes("\n");
            if (crossesLine || start < lineStart + prefixLen) event.preventDefault();
            return;
        }

        if (event.key === "Backspace") {
            const { lineStart, prefixLen } = lineBoundsAt(value, start);
            if (start <= lineStart + prefixLen) event.preventDefault();
            return;
        }

        // Delete (kursor bez zaznaczenia) - blokujemy kasowanie w głąb WŁASNEGO bulleta linii
        // albo znaku nowej linii na jej końcu (co scaliłoby ją z kolejną linią).
        const { lineStart, lineEnd, prefixLen } = lineBoundsAt(value, start);
        if (start < lineStart + prefixLen || (start === lineEnd && lineEnd < value.length)) {
            event.preventDefault();
        }
    });

    textArea.addEventListener("input", () => {
        const value = textArea.value;
        if (!value.includes("\n") && BULLET_LINE_RE.test(value)) return;

        const selStart = textArea.selectionStart;
        let charsSeen = 0;
        let cursorOffset = 0;
        let anyMissing = false;
        const fixedLines = value.split("\n").map((line) => {
            const lineStartPos = charsSeen;
            charsSeen += line.length + 1;
            if (BULLET_LINE_RE.test(line)) return line;
            anyMissing = true;
            if (lineStartPos < selStart) cursorOffset += 2;
            return `- ${line}`;
        });
        if (!anyMissing) return;

        textArea.value = fixedLines.join("\n");
        const newPos = selStart + cursorOffset;
        textArea.selectionStart = textArea.selectionEnd = newPos;
    });
}

export interface ExamQuestionFormOptions {
    onAutoClose?: () => void;
}

export function initExamQuestionForm(
    postId: number,
    container: HTMLElement,
    onQuestionAdded: (newQuestions: ExamQuestion[]) => void,
    options: ExamQuestionFormOptions = {},
): void {
    container.replaceChildren();

    const form = document.createElement("form");

    const textLabel = document.createElement("span");
    textLabel.className = "field-label-text comment-body-label";
    textLabel.textContent = "Treść pytań (każde w osobnej linii, zaczynając od „-” albo „*”):";

    // Wiele pytań naraz, po jednym w linii, z WYMAGANYM wiodącym bulletem (*, -, •) - na
    // wyraźną prośbę. Ten sam format co w źródłowym imporcie z wpia-wiki.pl (patrz
    // split_question_lines w pages/api/exam_questions.py, gdzie dzieje się właściwy podział i
    // walidacja - front nie duplikuje tej logiki, tylko pokazuje zwrócony błąd). Bullet nie
    // trzeba wpisywać ręcznie i nie da się go skasować - patrz attachAutoBulletList niżej.
    const textArea = document.createElement("textarea");
    textArea.required = true;
    textArea.rows = 4;
    attachAutoBulletList(textArea);

    const textError = document.createElement("span");
    textError.className = "error";

    const bodyFieldWrap = document.createElement("div");
    bodyFieldWrap.className = "comment-body-field";
    bodyFieldWrap.append(textLabel, textArea, textError);

    const submitBtn = document.createElement("button");
    submitBtn.type = "submit";
    submitBtn.textContent = "Dodaj pytanie";

    const bottomRow = document.createElement("div");
    bottomRow.className = "comment-form-bottom-row";
    bottomRow.appendChild(submitBtn);

    const feedback = document.createElement("p");
    const notifyPromptContainer = document.createElement("div");

    const fieldsWrap = makeCollapsible(bodyFieldWrap, bottomRow);
    const feedbackWrap = makeCollapsible(feedback, notifyPromptContainer);
    form.append(fieldsWrap.outer, feedbackWrap.outer);

    let feedbackHideTimer: number | undefined;

    form.addEventListener("submit", async (event: Event) => {
        event.preventDefault();
        textError.textContent = "";
        feedback.textContent = "";
        feedback.classList.remove("error");
        notifyPromptContainer.replaceChildren();
        if (feedbackHideTimer !== undefined) {
            window.clearTimeout(feedbackHideTimer);
            feedbackHideTimer = undefined;
        }
        feedbackWrap.outer.classList.remove("collapsible-collapsed");
        container.classList.remove("comment-form-collapsed");

        const textValue = textArea.value.trim();
        if (!textValue) {
            textError.textContent = "Podaj treść przynajmniej jednego pytania.";
            return;
        }

        submitBtn.disabled = true;
        feedback.textContent = "Zapisywanie...";

        const result = await api.createExamQuestion(postId, { text: textValue });

        if (result.ok) {
            const activeQuestions = result.data.questions.filter(q => q.status === "active");
            const pendingQuestions = result.data.questions.filter(q => q.status !== "active");
            const notifyTargets: { model: NotifyModelKey; pk: number }[] =
                pendingQuestions.map(q => ({ model: "exam_question", pk: q.id }));

            function scheduleFullHide(): void {
                if (feedbackHideTimer !== undefined) window.clearTimeout(feedbackHideTimer);
                feedbackHideTimer = window.setTimeout(() => {
                    feedbackWrap.outer.classList.add("collapsible-collapsed");
                    container.classList.add("comment-form-collapsed");
                    options.onAutoClose?.();
                }, 3000);
            }

            if (activeQuestions.length > 0) onQuestionAdded(activeQuestions);
            feedback.textContent = result.data.textmessage;

            const notifyPrompts: HTMLElement[] = notifyTargets.length > 0
                ? [renderPostHocNotifyPrompt(notifyTargets, scheduleFullHide)]
                : [];
            notifyPromptContainer.replaceChildren(...notifyPrompts);

            form.reset();
            fieldsWrap.outer.classList.add("collapsible-collapsed");
            if (notifyPrompts.length === 0) {
                feedbackHideTimer = window.setTimeout(() => {
                    feedbackWrap.outer.classList.add("collapsible-collapsed");
                    container.classList.add("comment-form-collapsed");
                    options.onAutoClose?.();
                }, 5000);
            }
        } else {
            feedback.classList.add("error");
            feedback.textContent = `Błąd: ${result.error}`;
        }
        submitBtn.disabled = false;
    });

    container.appendChild(form);
}
